// Core IndexedDB utility for offline-first data storage
import CryptoJS from 'crypto-js';

class OfflineDB {
  constructor() {
    this.dbName = 'SISRevolutionDB';
    this.version = 1;
    this.db = null;
    this.secretKey = localStorage.getItem('offline_secret') || this.generateSecretKey();
    this.syncQueue = [];
    this.conflictQueue = [];
    this.isOnline = navigator.onLine;
    this.lastSyncTime = localStorage.getItem('lastSyncTime') || Date.now();
    
    this.initializeDB();
    this.setupNetworkListeners();
    this.setupPeriodicSync();
  }

  // Generate encryption key for local data
  generateSecretKey() {
    const key = CryptoJS.lib.WordArray.random(256/8).toString();
    localStorage.setItem('offline_secret', key);
    return key;
  }

  // Encrypt sensitive data before storing
  encrypt(data) {
    if (typeof data === 'object') {
      data = JSON.stringify(data);
    }
    return CryptoJS.AES.encrypt(data, this.secretKey).toString();
  }

  // Decrypt data when retrieving
  decrypt(encryptedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.secretKey);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedData);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  // Initialize IndexedDB with all required object stores
  async initializeDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Students store
        if (!db.objectStoreNames.contains('students')) {
          const studentsStore = db.createObjectStore('students', { keyPath: '_id' });
          studentsStore.createIndex('schoolID', 'schoolID', { unique: false });
          studentsStore.createIndex('class', 'class_', { unique: false });
          studentsStore.createIndex('status', 'status', { unique: false });
          studentsStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Teachers store
        if (!db.objectStoreNames.contains('teachers')) {
          const teachersStore = db.createObjectStore('teachers', { keyPath: '_id' });
          teachersStore.createIndex('schoolID', 'schoolID', { unique: false });
          teachersStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Classes store
        if (!db.objectStoreNames.contains('classes')) {
          const classesStore = db.createObjectStore('classes', { keyPath: '_id' });
          classesStore.createIndex('schoolID', 'schoolID', { unique: false });
          classesStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Payments store
        if (!db.objectStoreNames.contains('payments')) {
          const paymentsStore = db.createObjectStore('payments', { keyPath: '_id' });
          paymentsStore.createIndex('schoolID', 'schoolID', { unique: false });
          paymentsStore.createIndex('studentID', 'studentID', { unique: false });
          paymentsStore.createIndex('date', 'date', { unique: false });
          paymentsStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Canteen store
        if (!db.objectStoreNames.contains('canteen')) {
          const canteenStore = db.createObjectStore('canteen', { keyPath: '_id' });
          canteenStore.createIndex('schoolID', 'schoolID', { unique: false });
          canteenStore.createIndex('studentID', 'studentID', { unique: false });
          canteenStore.createIndex('date', 'date', { unique: false });
          canteenStore.createIndex('lastModified', 'lastModified', { unique: false });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('operation', 'operation', { unique: false });
          syncStore.createIndex('priority', 'priority', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Conflict resolution store
        if (!db.objectStoreNames.contains('conflicts')) {
          const conflictsStore = db.createObjectStore('conflicts', { keyPath: 'id', autoIncrement: true });
          conflictsStore.createIndex('entityType', 'entityType', { unique: false });
          conflictsStore.createIndex('entityId', 'entityId', { unique: false });
          conflictsStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // User preferences store
        if (!db.objectStoreNames.contains('preferences')) {
          const prefsStore = db.createObjectStore('preferences', { keyPath: 'key' });
        }

        // Metadata store for tracking sync state
        if (!db.objectStoreNames.contains('metadata')) {
          const metaStore = db.createObjectStore('metadata', { keyPath: 'key' });
        }
      };
    });
  }

  // Generic method to add/update data with validation
  async addData(storeName, data, encrypt = false) {
    if (!this.db) await this.initializeDB();

    // Add metadata for sync tracking
    const timestamp = Date.now();
    const enrichedData = {
      ...data,
      lastModified: timestamp,
      needsSync: true,
      localVersion: this.generateVersion()
    };

    // Encrypt sensitive data
    if (encrypt) {
      enrichedData.encryptedData = this.encrypt(data);
      delete enrichedData.sensitiveFields;
    }

    // Validate data before storing
    if (!this.validateData(storeName, enrichedData)) {
      throw new Error(`Data validation failed for ${storeName}`);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(enrichedData);

      request.onsuccess = () => {
        this.addToSyncQueue('update', storeName, enrichedData._id, 'high');
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Generic method to get data by ID
  async getData(storeName, id, decrypt = false) {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        let result = request.result;
        if (result && decrypt && result.encryptedData) {
          const decryptedData = this.decrypt(result.encryptedData);
          result = { ...result, ...decryptedData };
          delete result.encryptedData;
        }
        resolve(result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all data from store with optional filtering
  async getAllData(storeName, filter = null, decrypt = false) {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        let results = request.result;

        // Apply filter if provided
        if (filter) {
          results = results.filter(filter);
        }

        // Decrypt if needed
        if (decrypt) {
          results = results.map(item => {
            if (item.encryptedData) {
              const decryptedData = this.decrypt(item.encryptedData);
              return { ...item, ...decryptedData };
            }
            return item;
          });
        }

        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Delete data by ID
  async deleteData(storeName, id) {
    if (!this.db) await this.initializeDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        this.addToSyncQueue('delete', storeName, id, 'high');
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Data validation based on store type
  validateData(storeName, data) {
    const validators = {
      students: (data) => data._id && data.schoolID && data.name,
      teachers: (data) => data._id && data.schoolID && data.name,
      classes: (data) => data._id && data.schoolID && data.className,
      payments: (data) => data._id && data.schoolID && data.studentID && data.amount,
      canteen: (data) => data._id && data.schoolID && data.studentID
    };

    const validator = validators[storeName];
    return validator ? validator(data) : true;
  }

  // Generate unique version for conflict resolution
  generateVersion() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Add operation to sync queue
  async addToSyncQueue(operation, storeName, entityId, priority = 'medium') {
    const queueItem = {
      operation,
      storeName,
      entityId,
      priority,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: 3
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['syncQueue'], 'readwrite');
      const store = transaction.objectStore('syncQueue');
      const request = store.add(queueItem);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Setup network status listeners
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Setup periodic background sync
  setupPeriodicSync() {
    // Sync every 30 seconds when online
    setInterval(() => {
      if (this.isOnline) {
        this.processSyncQueue();
      }
    }, 30000);

    // Full sync every 5 minutes
    setInterval(() => {
      if (this.isOnline) {
        this.performFullSync();
      }
    }, 300000);
  }

  // Process sync queue
  async processSyncQueue() {
    if (!this.isOnline) return;

    const queueItems = await this.getAllData('syncQueue');
    const prioritizedItems = queueItems.sort((a, b) => {
      const priorities = { high: 3, medium: 2, low: 1 };
      return priorities[b.priority] - priorities[a.priority];
    });

    for (const item of prioritizedItems) {
      try {
        await this.syncItem(item);
        await this.deleteData('syncQueue', item.id);
      } catch (error) {
        console.error('Sync failed for item:', item, error);
        await this.handleSyncFailure(item);
      }
    }
  }

  // Sync individual item with server
  async syncItem(item) {
    const { operation, storeName, entityId } = item;
    const data = await this.getData(storeName, entityId);

    // This would integrate with your actual API
    const apiEndpoints = {
      students: '/api/students',
      teachers: '/api/teachers',
      classes: '/api/class',
      payments: '/api/students/payments',
      canteen: '/api/students/canteen'
    };

    const endpoint = apiEndpoints[storeName];
    if (!endpoint) return;

    switch (operation) {
      case 'create':
      case 'update':
        // Send data to server
        break;
      case 'delete':
        // Delete from server
        break;
    }
  }

  // Handle sync failures
  async handleSyncFailure(item) {
    item.retryCount = (item.retryCount || 0) + 1;
    
    if (item.retryCount >= item.maxRetries) {
      // Move to conflict resolution
      await this.addToConflictQueue(item);
      await this.deleteData('syncQueue', item.id);
    } else {
      // Update retry count
      await this.addData('syncQueue', item);
    }
  }

  // Add item to conflict resolution queue
  async addToConflictQueue(item) {
    const conflictItem = {
      ...item,
      timestamp: Date.now(),
      status: 'pending_resolution'
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['conflicts'], 'readwrite');
      const store = transaction.objectStore('conflicts');
      const request = store.add(conflictItem);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Perform full synchronization
  async performFullSync() {
    try {
      // Sync all data types
      await this.syncAllStudents();
      await this.syncAllTeachers();
      await this.syncAllClasses();
      await this.syncAllPayments();
      await this.syncAllCanteen();
      
      // Update last sync time
      this.lastSyncTime = Date.now();
      localStorage.setItem('lastSyncTime', this.lastSyncTime.toString());
      
      // Emit sync completion event
      window.dispatchEvent(new CustomEvent('fullSyncComplete', {
        detail: { timestamp: this.lastSyncTime }
      }));
    } catch (error) {
      console.error('Full sync failed:', error);
      window.dispatchEvent(new CustomEvent('syncError', {
        detail: { error }
      }));
    }
  }

  // Sync methods for different data types
  async syncAllStudents() {
    // Implementation for syncing students
  }

  async syncAllTeachers() {
    // Implementation for syncing teachers
  }

  async syncAllClasses() {
    // Implementation for syncing classes
  }

  async syncAllPayments() {
    // Implementation for syncing payments
  }

  async syncAllCanteen() {
    // Implementation for syncing canteen data
  }

  // Get sync status and statistics
  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      lastSyncTime: this.lastSyncTime,
      queueLength: this.syncQueue.length,
      conflictCount: this.conflictQueue.length,
      timeSinceLastSync: Date.now() - this.lastSyncTime
    };
  }

  // Clear all offline data (for testing/reset)
  async clearAllData() {
    const stores = ['students', 'teachers', 'classes', 'payments', 'canteen', 'syncQueue', 'conflicts'];
    
    for (const storeName of stores) {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      await store.clear();
    }
  }
}

// Create singleton instance
const offlineDB = new OfflineDB();
export default offlineDB; 